import React, { type FC, type PointerEvent, useEffect, useRef, useState } from 'react'
import { Box, type SxProps, tableCellClasses, type Theme } from '@mui/material'

import { assert } from '../../../../utils'

import { type ColumnView, ConnectwareError, ConnectwareErrorType, setColumnWidth } from '../../../../domain'

import { RESIZE_CURSOR, useDraggingBodyStyle } from '../Dragging'
import { useAutoFitColumns } from './AutoFit'
import { useAllColumnNames, useColumn, useColumnView, useResizingColumnDispatcher, useShowsRows } from './State'
import { columnMinWidth } from './Widths'

/** In spacing steps: 8px, wide enough to grab, centred on the column's edge, while the line drawn in it stays 1px */
const GRIP_WIDTH = 2
/** In px, as a bare 1 in `sx` would be a 100% width */
const GRIP_LINE_WIDTH = 1
/** In px: a press moving less than this, as a double click's jitter does, is no drag yet */
const DRAG_DEAD_ZONE = 4

const gripStyle = (dragging: boolean): SxProps<Theme> => (theme) => {
    const line = (visible: boolean): SxProps<Theme> => ({
        content: '""',
        width: `${GRIP_LINE_WIDTH}px`,
        height: '100%',
        backgroundColor: visible ? theme.palette.grey[300] : 'transparent',
    })

    return {
        position: 'absolute',
        top: 0,
        right: theme.spacing(-GRIP_WIDTH / 2),
        zIndex: 1,
        display: 'flex',
        justifyContent: 'center',
        width: theme.spacing(GRIP_WIDTH),
        height: '100%',
        cursor: RESIZE_CURSOR,
        touchAction: 'none',
        '&::after': line(dragging),
        [`.${tableCellClasses.head}:hover > &::after`]: line(true),
    }
}

type Drag = Readonly<{ pointerId: number, startX: number, startWidth: number, minWidth: number }> & { width: number | null }

/**
 * Drags the right edge of a column's header cell, with the pointer captured, never below the column's minimum width.
 * The width is only rendered while dragging, and is committed to the column view once, when the pointer is released;
 * a cancelled or interrupted drag leaves the view as it was.
 * Only the primary pointer's main button drags, and only the pointer that started the drag moves it.
 * A move only starts the drag once past a small dead zone, so a double click, which fits the column to its content, commits no drag.
 * Without data rows the double click fits nothing, as only the header would be measured.
 * Mouse and touch only: it isn't focusable, keyboard users size columns through the column view menu
 */
export const ResizeGrip: FC<Readonly<{ name: string, onChange: (view: ColumnView) => void }>> = ({ name, onChange }) => {
    const column = useColumn(name)
    const names = useAllColumnNames()
    const view = useColumnView()
    const setResizing = useResizingColumnDispatcher()
    const autoFit = useAutoFitColumns(onChange)
    const showsRows = useShowsRows()
    const drag = useRef<Drag | null>(null)
    const [dragging, setDragging] = useState(false)

    useDraggingBodyStyle(dragging)

    /** Unmounted mid-drag, the table would otherwise keep rendering the uncommitted width */
    useEffect(() => {
        if (!dragging) {
            return
        }

        return () => {
            if (drag.current) {
                setResizing(null)
            }
        }
    }, [dragging])

    const findDrag = (event: PointerEvent<HTMLElement>): Drag | null => (drag.current?.pointerId === event.pointerId ? drag.current : null)

    const end = (): void => {
        drag.current = null
        setDragging(false)
        setResizing(null)
    }

    const onPointerDown = (event: PointerEvent<HTMLElement>): void => {
        if (event.button !== 0 || !event.isPrimary || drag.current) {
            return
        }

        /** Keeps the press from selecting text or moving the focus */
        event.preventDefault()

        const cell = event.currentTarget.parentElement
        assert(cell, new ConnectwareError(ConnectwareErrorType.UNEXPECTED, 'A resize grip must be inside its header cell', { name }))

        event.currentTarget.setPointerCapture(event.pointerId)
        drag.current = {
            pointerId: event.pointerId,
            startX: event.clientX,
            startWidth: cell.getBoundingClientRect().width,
            minWidth: columnMinWidth(column),
            width: null,
        }
        setDragging(true)
    }

    const onPointerMove = (event: PointerEvent<HTMLElement>): void => {
        const current = findDrag(event)
        if (!current) {
            return
        }

        /** No button pressed anymore means the release was missed, so the drag is over, uncommitted */
        if (event.buttons === 0) {
            end()
            return
        }

        const offset = event.clientX - current.startX
        if (current.width === null && Math.abs(offset) < DRAG_DEAD_ZONE) {
            return
        }

        current.width = Math.max(current.minWidth, current.startWidth + offset)
        setResizing({ name, width: current.width, minWidth: current.minWidth })
    }

    const onPointerUp = (event: PointerEvent<HTMLElement>): void => {
        const current = findDrag(event)
        if (!current) {
            return
        }

        if (current.width !== null) {
            onChange(setColumnWidth(names, view, name, current.width, current.minWidth))
        }

        end()
    }

    /** A cancel, or a capture lost without a release, ends the drag uncommitted; after a release there's no drag left to end */
    const onInterrupt = (event: PointerEvent<HTMLElement>): void => {
        if (findDrag(event)) {
            end()
        }
    }

    return (
        <Box
            component="span"
            aria-hidden
            data-testid={`column-header-resize-${name}`}
            sx={gripStyle(dragging)}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onInterrupt}
            onLostPointerCapture={onInterrupt}
            onDoubleClick={(event) => {
                if (showsRows) {
                    autoFit(event.currentTarget, [name])
                }
            }}
        />
    )
}
